import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { GlobalsService } from "@services";
import { TeamChatFabPin, TeamChatStore } from "@stores";
import { ITeamChatConversationModel, ITeamChatConversationSummaryModel, ITeamChatMemberModel, ITeamChatMessageModel, ITeamChatTeamModel } from "@models";
import { TeamChatMentionAutocomplete } from "@shared-components/chat-mentions";
import { EmojiAutocomplete, IEmojiShortcode } from "@shared-components/emoji-picker";
import Swal from "sweetalert2";

type TeamChatView = "list" | "chat" | "members";

/**
 * Company team chat.  Lives in AppComponent rather than a route so the docked panel survives
 * page changes, and shows up either as a bottom-left FAB + flyout or as a panel docked to the right
 * edge (in which case the FAB goes away and the rest of the site shrinks -- see the
 * --team-chat-dock-width custom property, which this component owns).
 */
@Component({
	selector: "team-chat-widget",
	templateUrl: "team-chat-widget.component.html",
	styleUrls: ["team-chat-widget.component.css"],
	standalone: false
})
export class TeamChatWidgetComponent implements OnInit, OnDestroy {
	@ViewChild("messagesRef") messagesRef: ElementRef;
	@ViewChild("newMessageRef") newMessageRef: ElementRef;
	@ViewChild("fileInputRef") fileInputRef: ElementRef;

	/** Matches .oc-dock in the stylesheet -- the site is shrunk by exactly this much. */
	private static readonly DOCK_WIDTH = 340;

	/** Matches .oc-fab / .oc-flyout in the stylesheet.  The flyout's height is a maximum: a short
	 *  window gets a shorter panel. */
	private static readonly FAB_SIZE = 52;
	private static readonly FLYOUT_WIDTH = 360;
	private static readonly FLYOUT_HEIGHT = 520;
	private static readonly FLYOUT_MIN_HEIGHT = 260;

	/** Gap between the bubble and the flyout, and the least the two will sit from the window edge. */
	private static readonly FLYOUT_GAP = 10;
	private static readonly VIEWPORT_MARGIN = 8;

	/** Where the bubble sits until it's been dragged: parked against the right edge, this far up from
	 *  the bottom of the window.  Clear of the footer bar, and clear of the bottom-right corner where
	 *  the browser puts its own status/download popups. */
	private static readonly FAB_DEFAULT_BOTTOM = 100;

	/** A press that travels less than this is somebody clicking the bubble, not moving it -- below it
	 *  the click still opens the panel, above it the release is swallowed. */
	private static readonly DRAG_THRESHOLD = 4;

	/** Let go this close to the left or right edge and the bubble sticks to it.  Generous on purpose:
	 *  parking it against the side is the thing people are aiming for, and hitting a wall is easier
	 *  than hitting a line -- but narrow enough that the middle of the screen stays a place you can
	 *  leave it. */
	private static readonly EDGE_SNAP_DISTANCE = 60;

	/** Faces the chat header has room for before the rest turn into a "+N". */
	private static readonly HEADER_FACE_LIMIT = 5;

	/** How close to the top of the thread counts as "scrolled up" and fetches the previous page. */
	private static readonly LOAD_MORE_SCROLL_THRESHOLD = 40;

	/** Slack at the bottom of the thread that still counts as being at the bottom -- scroll heights
	 *  come back fractional, so an exact comparison never holds. */
	private static readonly PINNED_TO_BOTTOM_TOLERANCE = 24;

	/** How long the thread has to stop moving before it's shown.  Each photo that loads scrolls it
	 *  again, so this is really "nothing has landed for a moment". */
	private static readonly THREAD_SETTLE_DELAY = 150;

	/** Longest the thread is ever hidden.  A photo that neither loads nor errors would otherwise leave
	 *  the chat blank for good -- late jank beats no messages. */
	private static readonly THREAD_REVEAL_TIMEOUT = 2500;

	private subscriptions: Subscription[] = [];

	/* Whether the thread is sitting at the bottom, and so should follow content down as it grows.
	 * Only the reader's own scrolling clears it.
	 *
	 * This is what opening a chat that ends in photos depends on.  A photo has no height until it has
	 * loaded, so the scroll that runs on open lands at the bottom of a thread that is still missing all
	 * of them, and each one that arrives pushes the real bottom further down.  Measuring how far off
	 * the bottom we are instead can't work: a screen of unloaded photos puts it further away than any
	 * sane threshold, so the follow gives up and strands the thread half way up. */
	private isPinnedToBottom: boolean = true;

	/* Whether the thread is shown yet.  A chat opens scrolled to the bottom, but it takes a frame (and,
	 * with photos in it, several more as each one loads and pushes the bottom further down) to actually
	 * get there -- all of which is visible as the thread jumping about.  It stays hidden until it has
	 * stopped moving, so what appears is the finished article. */
	isThreadReady: boolean = false;
	private threadSettleTimer: any = null;
	private threadRevealTimer: any = null;

	userId: number = GlobalsService.userInfo.userId;

	conversations: ITeamChatConversationSummaryModel[] = [];
	activeConversation: ITeamChatConversationModel = null;
	unreadCount: number = 0;

	// Who the chat header draws as faces.  Held as fields rather than worked out by a getter so the
	// array keeps its identity between change detection runs -- a fresh array every cycle would have
	// *ngFor rebuild the avatars, and the profile photos would flicker as they reloaded.
	headerMembers: ITeamChatMemberModel[] = [];
	hiddenMemberCount: number = 0;

	isFabOpen: boolean = false;
	isDocked: boolean = false;
	isCollapsed: boolean = false;

	// Where the bubble is, in viewport pixels, and where the flyout has to go to hang off it.  Both
	// are written into style bindings, so they're held as objects that only change when the position
	// actually does -- a fresh object per change detection run would re-write the styles constantly.
	fabStyle: { left: string, top: string } = { left: "0", top: "0" };
	flyoutStyle: { left: string, top: string, height: string, transformOrigin: string } = { left: "0", top: "0", height: "0", transformOrigin: "bottom right" };

	isDraggingFab: boolean = false;

	/** Bubble position, as the top-left corner of the button.  Set for real in ngOnInit. */
	private fabX: number = 0;
	private fabY: number = 0;

	/** Whether that position is one the user chose.  A bubble still sitting where it was put stays in
	 *  its corner as the window is resized; a dragged one keeps the spot it was dragged to. */
	private hasCustomFabPosition: boolean = false;

	/** The edge the bubble is stuck to, if it was let go against one.  Read by the template, which
	 *  squares off the corners on that side so it reads as parked rather than merely near the edge. */
	pinnedSide: TeamChatFabPin = null;

	/** Live drag: where in the button the pointer took hold, and where the press started (which is
	 *  what says whether the release was a click or the end of a move). */
	private dragPointerId: number = null;
	private dragOffsetX: number = 0;
	private dragOffsetY: number = 0;
	private dragStartX: number = 0;
	private dragStartY: number = 0;
	private hasDragMoved: boolean = false;

	view: TeamChatView = "list";

	newMessage: string = "";
	isSending: boolean = false;

	isEmojiPickerOpen: boolean = false;
	isUploading: boolean = false;

	/** The "@" autocomplete.  Offers everybody in the company, not just this chat's members. */
	mentions: TeamChatMentionAutocomplete = new TeamChatMentionAutocomplete(() => this.teamChatStore.getAvailableUsers(), this.userId);

	/** The ":" autocomplete -- ":wink" offers the emoji, ":wink:" is the emoji.  For the people who
	 *  know the name of the one they want and would rather type it than go hunting in the picker. */
	emojis: EmojiAutocomplete = new EmojiAutocomplete();

	// Rename (anybody in the group can do it)
	isRenaming: boolean = false;
	renameText: string = "";

	// New chat / add people
	availableUsers: ITeamChatMemberModel[] = [];
	availableTeams: ITeamChatTeamModel[] = [];
	memberSearchText: string = "";
	/** Picked by name.  Somebody a ticked team already covers can be in here as well -- that pins them
	 *  to the chat, so they stay in it if they later come off the team. */
	selectedUserIds: number[] = [];
	selectedTeamIds: number[] = [];

	// Whether each half of the picker is rolled up.  A company with twenty teams and eighty people
	// gives both lists more rows than the panel can show at once, and folding the half you're not
	// picking from is what brings the other half back into view.
	//
	// Held here rather than persisted: this outlives the picker being closed and reopened, which
	// covers picking several chats in a row, and a fresh session opens on both lists showing.
	isCurrentCollapsed: boolean = false;
	isTeamsCollapsed: boolean = false;
	isPeopleCollapsed: boolean = false;

	// What the chat already had in it when the picker was opened -- the teams on it and everybody in
	// it, however they got there.  Taken once, on the way in, rather than read live: rows that moved
	// up here the moment they were ticked would jump out from under whoever was ticking them.
	//
	// Empty for a brand new chat, which is what keeps the section off that one.
	private currentTeamIds: number[] = [];
	private currentUserIds: number[] = [];

	// Who the ticked teams bring, worked out when the ticks change rather than per change detection
	// run: the people list asks about every name it draws, and both of these are read once a row.
	private teamCoveredUserIds: number[] = [];
	private teamNamesByUserId: { [userId: number]: string } = {};
	newChatName: string = "";
	/** null while building a brand new chat, otherwise the chat we're editing the roster of. */
	membersForConversationId: number = null;

	constructor(public teamChatStore: TeamChatStore) {
	}

	ngOnInit() {
		this.conversations = this.teamChatStore.getConversations();
		this.unreadCount = this.teamChatStore.getUnreadMessagesCount();
		this.isDocked = this.teamChatStore.isDocked;
		this.isCollapsed = this.teamChatStore.isCollapsed;
		this.isFabOpen = this.teamChatStore.isFabOpen;
		this.applyDockWidth();
		this.applyConversationOnScreen();
		this.restoreFabPosition();

		// Back into whichever chat was being read before the refresh.  The store restores it during
		// app startup, so by now it's either sitting there or we open on the conversation list.
		if (this.teamChatStore.getActiveConversation()) {
			this.setView("chat");
			this.beginThreadOpen();
		}
		else {
			this.setView("list");
		}

		this.subscriptions.push(this.teamChatStore.conversations$.subscribe(conversations => (this.conversations = conversations)));
		this.subscriptions.push(this.teamChatStore.unreadMessagesCount$.subscribe(unreadCount => (this.unreadCount = unreadCount)));
		this.subscriptions.push(
			this.teamChatStore.activeConversation$.subscribe(activeConversation => {
				this.activeConversation = activeConversation;
				this.applyHeaderMembers();

				if (activeConversation) {
					this.scrollToBottom();
					return;
				}

				// Removed from the chat -- taken off it by hand, or dropped from a team that is on it --
				// while sitting in it.  The store drops the conversation and publishes null; leaving the
				// reader looking at an empty thread they can no longer post to isn't an answer, so put them
				// back on the chat list.  Not from the new-chat picker, which has no active conversation by
				// design and mustn't be closed out from under whoever is filling it in.
				if (this.view === "chat" || (this.view === "members" && this.membersForConversationId))
					this.showList();
			})
		);
		this.subscriptions.push(
			this.teamChatStore.isDocked$.subscribe(isDocked => {
				this.isDocked = isDocked;
				this.applyDockWidth();
				this.applyConversationOnScreen();
			})
		);
		this.subscriptions.push(
			this.teamChatStore.isCollapsed$.subscribe(isCollapsed => {
				this.isCollapsed = isCollapsed;
				this.applyDockWidth();
				this.applyConversationOnScreen();
			})
		);
		this.subscriptions.push(
			this.teamChatStore.isFabOpen$.subscribe(isFabOpen => {
				this.isFabOpen = isFabOpen;
				this.applyConversationOnScreen();
			})
		);
		this.subscriptions.push(this.teamChatStore.newMessage$.subscribe(() => this.scrollToBottom()));
	}

	ngOnDestroy() {
		clearTimeout(this.threadSettleTimer);
		clearTimeout(this.threadRevealTimer);
		this.stopFabDrag();
		this.teamChatStore.setConversationOnScreen(false);
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		document.documentElement.style.setProperty("--team-chat-dock-width", "0px");
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// FAB / docking
	////////////////////////////////////////////////////////////////////////////////////////////////
	toggleFab() {
		this.teamChatStore.setFabOpen(!this.isFabOpen);

		if (this.isFabOpen) this.reopenPanel();
		else this.applyConversationOnScreen();
	}

	closeFab() {
		this.teamChatStore.setFabOpen(false);
		this.applyConversationOnScreen();
	}

	dock() {
		this.teamChatStore.setFabOpen(false);
		this.teamChatStore.setDocked(true);
	}

	undock() {
		this.teamChatStore.setDocked(false);
		this.teamChatStore.setFabOpen(true);
	}

	toggleCollapsed() {
		this.teamChatStore.setCollapsed(!this.isCollapsed);

		if (!this.isCollapsed) this.reopenPanel();
	}

	/**
	 * Bringing the panel back after it's been closed down.  The flyout and the docked panel are both
	 * *ngIf'd out while hidden, so this runs against a panel that's about to be built from scratch:
	 * nothing survives but the conversation itself, hence the reset to the bottom.
	 *
	 * Where it lands, in order:
	 *   1. the chat with something unread in it -- that badge is why they're opening the panel;
	 *   2. failing that, whatever they were last reading;
	 *   3. failing that, the conversation list.
	 * The member picker and a half-built new chat are never reopened into -- they drop to the list
	 * rather than coming back mid-task.
	 */
	private reopenPanel() {
		this.isRenaming = false;

		// conversations is sorted newest activity first, so with several unread they land in the
		// liveliest.  A muted chat never carries an unread count, so it's left out of this for free.
		const unreadConversation = this.conversations.find(x => x.unreadMessages > 0);
		if (unreadConversation) {
			this.selectConversation(unreadConversation);
			return;
		}

		if (this.view === "chat" && this.activeConversation) {
			// Anything that came in while it was closed is badged; putting it back on screen reads it.
			this.teamChatStore.markActiveConversationRead();
			this.scrollToBottom();
			return;
		}

		this.showList();
	}

	/* A collapsed panel gives its space back to the page, so the width is only applied while the
	 * panel is actually on screen. */
	/**
	 * Whether the reader can actually see the open conversation, which decides whether an arriving
	 * message counts as read and whether it chimes.  Docked counts only while it isn't collapsed;
	 * undocked only while the flyout is open; and either way only on the chat view.
	 */
	private applyConversationOnScreen() {
		const isPanelShowing = this.isDocked ? !this.isCollapsed : this.isFabOpen;

		this.teamChatStore.setConversationOnScreen(isPanelShowing && this.view === "chat");
	}

	private setView(view: TeamChatView) {
		this.view = view;
		this.applyConversationOnScreen();
	}

	private applyDockWidth() {
		const width = this.isDocked && !this.isCollapsed ? `${TeamChatWidgetComponent.DOCK_WIDTH}px` : "0px";
		document.documentElement.style.setProperty("--team-chat-dock-width", width);
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// Dragging the bubble
	////////////////////////////////////////////////////////////////////////////////////////////////
	/* The area a position: fixed element actually has to sit in -- window.innerWidth/Height count the
	 * scrollbars, which the bubble can't be placed under, and pointer coordinates don't. */
	private get viewportWidth(): number {
		return document.documentElement.clientWidth || window.innerWidth;
	}

	private get viewportHeight(): number {
		return document.documentElement.clientHeight || window.innerHeight;
	}

	/** Wherever it was left last time, or parked on the right edge the first time round. */
	private restoreFabPosition() {
		const stored = this.teamChatStore.getFabPosition();

		if (stored) {
			this.hasCustomFabPosition = true;
			this.pinnedSide = stored.pinnedSide;

			// A pinned bubble goes back onto its edge rather than to the x it was saved at: that x was
			// measured against whatever window it was pinned in, and this one may be a different width.
			this.moveFab(this.pinnedSide ? this.pinnedX(this.pinnedSide) : stored.x, stored.y);
			return;
		}

		// Starts pinned, the same as if it had been dropped against the right edge -- which is where
		// the docked panel lives, so the bubble opens out of the side the chat already belongs to.
		this.pinnedSide = "right";

		this.moveFab(
			this.pinnedX(this.pinnedSide),
			this.viewportHeight - TeamChatWidgetComponent.FAB_DEFAULT_BOTTOM - TeamChatWidgetComponent.FAB_SIZE);
	}

	/**
	 * Takes hold of the bubble.  Pointer events rather than mouse ones so a touchscreen laptop drags
	 * it too, and the capture means the drag keeps working when the pointer outruns the button.
	 *
	 * Nothing has moved yet at this point, so this can't tell a click from a drag -- that's decided
	 * on release, in onFabDragEnd.
	 */
	startFabDrag(event: PointerEvent) {
		// Left button only: right-clicking the bubble shouldn't pick it up.
		if (event.button !== 0) return;

		// Otherwise the press selects the page text behind the button as it travels.
		event.preventDefault();

		this.dragPointerId = event.pointerId;
		this.dragOffsetX = event.clientX - this.fabX;
		this.dragOffsetY = event.clientY - this.fabY;
		this.dragStartX = event.clientX;
		this.dragStartY = event.clientY;
		this.hasDragMoved = false;

		(<HTMLElement>event.target)?.setPointerCapture?.(event.pointerId);

		// Only hooked up for the length of the drag: as permanent host listeners these would run
		// change detection on every mouse move anywhere in the app.
		document.addEventListener("pointermove", this.onFabDragMove);
		document.addEventListener("pointerup", this.onFabDragEnd);
		document.addEventListener("pointercancel", this.onFabDragEnd);
	}

	private onFabDragMove = (event: PointerEvent) => {
		if (this.dragPointerId === null || event.pointerId !== this.dragPointerId) return;

		// Small wobbles during a click aren't a drag: the bubble stays put until the pointer has
		// travelled far enough to mean it, and from then on the whole gesture is a move.
		if (!this.hasDragMoved) {
			const travelled = Math.max(Math.abs(event.clientX - this.dragStartX), Math.abs(event.clientY - this.dragStartY));
			if (travelled < TeamChatWidgetComponent.DRAG_THRESHOLD) return;

			this.hasDragMoved = true;
			this.isDraggingFab = true;

			// It's in hand now, so it's not parked anywhere.  Cleared as the drag starts rather than on
			// release so the bubble keeps its margin off the edge while it's being moved -- it snaps
			// flush at the end, and only if it was let go there.
			this.pinnedSide = null;
		}

		this.moveFab(event.clientX - this.dragOffsetX, event.clientY - this.dragOffsetY);
	};

	private onFabDragEnd = (event: PointerEvent) => {
		if (this.dragPointerId === null || event.pointerId !== this.dragPointerId) return;

		const wasDragged = this.hasDragMoved;

		this.stopFabDrag();

		// A press that never went anywhere is a click on the bubble, and opens the panel.  One that
		// did is a move, and is only ever a move -- the panel is left as it was.
		if (wasDragged) {
			this.hasCustomFabPosition = true;
			this.applyEdgePin();
			this.teamChatStore.setFabPosition(this.fabX, this.fabY, this.pinnedSide);
		}
		else {
			this.toggleFab();
		}
	};

	private stopFabDrag() {
		this.dragPointerId = null;
		this.isDraggingFab = false;
		this.hasDragMoved = false;

		document.removeEventListener("pointermove", this.onFabDragMove);
		document.removeEventListener("pointerup", this.onFabDragEnd);
		document.removeEventListener("pointercancel", this.onFabDragEnd);
	}

	/**
	 * A window that's been made smaller can leave the bubble off the edge of it -- and with it the
	 * flyout, which is sized to the window as well.  A bubble that's never been moved goes back to
	 * its default spot on the right edge, so it tracks the resize; a dragged one is only clamped back
	 * into view, and the saved position is left alone -- shrinking the window for a moment shouldn't
	 * throw away where the user put it.
	 */
	@HostListener("window:resize")
	onWindowResize() {
		// A bubble nobody has moved goes back to its default spot, so it keeps its distance from the
		// bottom of the window rather than staying at whatever pixel that used to be.  Checked before
		// the pin, because that default is itself a pinned position.
		if (!this.hasCustomFabPosition) this.restoreFabPosition();
		// A pinned bubble is pinned to an edge, not to a coordinate -- it rides the edge as it moves.
		else if (this.pinnedSide) this.moveFab(this.pinnedX(this.pinnedSide), this.fabY);
		else this.moveFab(this.fabX, this.fabY);
	}

	/**
	 * Sticks the bubble to whichever side it was let go against, or leaves it where it is.  Run on
	 * release rather than during the drag: snapping under the cursor would fight whoever is trying to
	 * put it somewhere in between.
	 */
	private applyEdgePin() {
		const distanceFromLeft = this.fabX;
		const distanceFromRight = this.viewportWidth - (this.fabX + TeamChatWidgetComponent.FAB_SIZE);

		// Nearest edge wins, which only matters on a window narrow enough for both to be in range.
		if (distanceFromLeft <= TeamChatWidgetComponent.EDGE_SNAP_DISTANCE && distanceFromLeft <= distanceFromRight)
			this.pinnedSide = "left";
		else if (distanceFromRight <= TeamChatWidgetComponent.EDGE_SNAP_DISTANCE)
			this.pinnedSide = "right";
		else
			this.pinnedSide = null;

		if (this.pinnedSide)
			this.moveFab(this.pinnedX(this.pinnedSide), this.fabY);
	}

	/** Flush against that edge -- no margin, which is the difference between parked and merely near. */
	private pinnedX(side: TeamChatFabPin): number {
		return (side === "left") ? 0 : this.viewportWidth - TeamChatWidgetComponent.FAB_SIZE;
	}

	/** The one place the bubble's position is set: clamps it into the window, then hangs the flyout
	 *  off wherever it ended up. */
	private moveFab(x: number, y: number) {
		const margin = TeamChatWidgetComponent.VIEWPORT_MARGIN;
		const size = TeamChatWidgetComponent.FAB_SIZE;

		// The margin is what keeps a loose bubble off the edge; a pinned one is being held against it
		// on purpose, so sideways it gets none.  Vertically it keeps its margin either way.
		const horizontalMargin = this.pinnedSide ? 0 : margin;

		this.fabX = Math.min(Math.max(horizontalMargin, x), Math.max(horizontalMargin, this.viewportWidth - horizontalMargin - size));
		this.fabY = Math.min(Math.max(margin, y), Math.max(margin, this.viewportHeight - margin - size));

		this.fabStyle = { left: `${this.fabX}px`, top: `${this.fabY}px` };
		this.applyFlyoutPosition();
	}

	/**
	 * Puts the flyout beside the bubble: above it and right-aligned with it by default, which is
	 * where it has always sat in the bottom-right corner.  Near an edge there isn't room for that, so
	 * it flips to the other side of the bubble (below it, or left-aligned) and, failing even that, is
	 * clamped into the window.  The corner it grows out of follows, so it still rises out of the
	 * button rather than out of thin air.
	 */
	private applyFlyoutPosition() {
		const margin = TeamChatWidgetComponent.VIEWPORT_MARGIN;
		const gap = TeamChatWidgetComponent.FLYOUT_GAP;
		const width = TeamChatWidgetComponent.FLYOUT_WIDTH;
		const size = TeamChatWidgetComponent.FAB_SIZE;

		const height = Math.max(
			TeamChatWidgetComponent.FLYOUT_MIN_HEIGHT,
			Math.min(TeamChatWidgetComponent.FLYOUT_HEIGHT, this.viewportHeight - (2 * margin)));

		// Above the bubble if it fits, otherwise below it.
		const isAbove = this.fabY - gap - height >= margin;
		let top = isAbove ? this.fabY - gap - height : this.fabY + size + gap;
		top = Math.min(Math.max(margin, top), Math.max(margin, this.viewportHeight - margin - height));

		// Right edges lined up if that leaves the panel on screen, otherwise left edges.
		const isRightAligned = this.fabX + size - width >= margin;
		let left = isRightAligned ? this.fabX + size - width : this.fabX;
		left = Math.min(Math.max(margin, left), Math.max(margin, this.viewportWidth - margin - width));

		this.flyoutStyle = {
			left: `${left}px`,
			top: `${top}px`,
			height: `${height}px`,
			// Whichever of the panel's corners is nearest the bubble.
			transformOrigin: `${isAbove ? "bottom" : "top"} ${left + (width / 2) > this.fabX + (size / 2) ? "left" : "right"}`
		};
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// Conversation list
	////////////////////////////////////////////////////////////////////////////////////////////////
	showList() {
		this.setView("list");
		this.isRenaming = false;
		this.teamChatStore.clearActiveConversation();
	}

	async selectConversation(conversation: ITeamChatConversationSummaryModel) {
		this.setView("chat");
		this.beginThreadOpen();
		this.isRenaming = false;
		this.newMessage = "";
		this.mentions.reset();
		this.emojis.reset();
		await this.teamChatStore.loadActiveConversation(conversation.teamChatConversationId);
		this.scrollToBottom();
		setTimeout(() => this.newMessageRef?.nativeElement?.focus(), 100);
	}

	/** The X on a chat row -- closes that chat down for this user until it gets a new message. */
	async closeConversation(event: MouseEvent, conversation: ITeamChatConversationSummaryModel) {
		event.stopPropagation();

		await this.teamChatStore.hideConversation(conversation.teamChatConversationId);

		if (this.view === "chat" && !this.activeConversation) this.showList();
	}

	getDisplayName(conversation: ITeamChatConversationSummaryModel | ITeamChatConversationModel): string {
		return this.teamChatStore.getDisplayName(conversation);
	}

	/** The bell on a chat row -- stops it badging and chiming for this user only. */
	async toggleMute(event: MouseEvent, conversation: ITeamChatConversationSummaryModel) {
		event.stopPropagation();

		await this.teamChatStore.setMuted(conversation.teamChatConversationId, !conversation.isMuted);
	}

	/**
	 * Whether the header draws faces or a title.  A name somebody chose wins, and so does a team --
	 * "Install" is what the chat is, where the six faces it expands into say only that it's a crowd.
	 * Faces are what's left, and with none of those (a chat with only you in it) the title is "Just
	 * you" rather than an empty header.
	 */
	get showHeaderFaces(): boolean {
		return !this.activeConversation?.name && (this.activeConversation?.teams || []).length === 0 && this.headerMembers.length > 0;
	}

	/** Everybody in the active chat but me, capped at what the header has room to draw. */
	private applyHeaderMembers() {
		const otherMembers = (this.activeConversation?.members || []).filter(x => x.userId !== this.userId);

		this.headerMembers = otherMembers.slice(0, TeamChatWidgetComponent.HEADER_FACE_LIMIT);
		this.hiddenMemberCount = Math.max(0, otherMembers.length - TeamChatWidgetComponent.HEADER_FACE_LIMIT);
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// Messages
	////////////////////////////////////////////////////////////////////////////////////////////////
	onKeyDown(event: KeyboardEvent) {
		// While the "@" list is up it owns the keyboard: the arrows walk it, Enter picks rather than
		// sends, and Esc puts it away and hands typing back.
		if (this.mentions.isOpen) {
			switch (event.key) {
				case "ArrowDown":
					event.preventDefault();
					this.mentions.moveSelection(1);
					return;
				case "ArrowUp":
					event.preventDefault();
					this.mentions.moveSelection(-1);
					return;
				case "Enter":
				case "Tab":
					event.preventDefault();
					this.applySelectedMention(this.mentions.selectedUser);
					return;
				case "Escape":
					event.preventDefault();
					this.mentions.dismiss();
					return;
			}
		}

		// Same again for the ":" list.  Only one of the two is ever up -- one is opened by an "@" and
		// the other by a ":" -- so which is checked first doesn't decide anything.
		if (this.emojis.isOpen) {
			switch (event.key) {
				case "ArrowDown":
					event.preventDefault();
					this.emojis.moveSelection(1);
					return;
				case "ArrowUp":
					event.preventDefault();
					this.emojis.moveSelection(-1);
					return;
				case "Enter":
				case "Tab":
					event.preventDefault();
					this.applySelectedEmoji(this.emojis.selectedShortcode);
					return;
				case "Escape":
					event.preventDefault();
					this.emojis.dismiss();
					return;
			}
		}

		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			this.sendMessage();
			return;
		}

		// The caret has already moved for arrows/home/end by the time the input event would fire, so
		// re-checking after the key lands keeps the list honest about where the "@" is.
		setTimeout(() => this.onComposerChanged());
	}

	async sendMessage() {
		const body = (this.newMessage || "").trim();
		if (!body || !this.activeConversation || this.isSending) return;

		this.isSending = true;
		this.newMessage = "";

		// The message has gone -- the picker was for writing it.
		this.isEmojiPickerOpen = false;

		// Names the user picked become "@[Full Name](id)" here -- the composer only ever showed the
		// readable version.  Anybody tagged who isn't in the chat is added by the API.
		// Anything still spelled out in text -- a ":name:" or a face that was never followed by a
		// space -- becomes its emoji on the way out.  Before the mention markup, which brings colons
		// and brackets of its own into the body that have nothing to do with emojis.
		const bodyWithMentions = this.mentions.applyMentions(this.emojis.applyTextEmojis(body));

		try {
			const message = await this.teamChatStore.sendMessage(this.activeConversation.teamChatConversationId, bodyWithMentions);

			// In debug the SignalR connection is off, so the echo back through the hub never
			// arrives -- drop our own message in directly rather than leaving it looking unsent.
			if (message && this.activeConversation && !this.activeConversation.messages.some(x => x.teamChatMessageId === message.teamChatMessageId)) {
				this.activeConversation.messages.push(message);
				this.scrollToBottom();
			}
		} finally {
			this.isSending = false;
		}
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// @mentions
	////////////////////////////////////////////////////////////////////////////////////////////////
	/** Anything that moves the caret or the text re-reads what's being typed after an "@". */
	onComposerChanged() {
		const textArea = <HTMLTextAreaElement>this.newMessageRef?.nativeElement;
		const caret = textArea?.selectionStart ?? (this.newMessage || "").length;

		this.mentions.update(this.newMessage || "", caret);

		// A name typed out in full and closed off (":wink:") is already answered -- it becomes the
		// emoji here and now, rather than opening a list to confirm what was just spelled out.  A
		// typed face (";) ") is the same story once the space after it says it was meant as a face.
		const completed = this.emojis.completeShortcode(this.newMessage || "", caret) || this.emojis.completeEmoticon(this.newMessage || "", caret);
		if (completed) {
			this.applyComposerText(completed);
			return;
		}

		this.emojis.update(this.newMessage || "", caret);

		// Both hang in the same place above the composer, and somebody typing the name of an emoji has
		// stopped looking for it in the grid.
		if (this.emojis.isOpen)
			this.isEmojiPickerOpen = false;
	}

	/** Tapping an emoji in the list.  mousedown rather than click, for the same reason as the names:
	 *  the textarea mustn't lose focus first. */
	selectEmoji(event: Event, shortcode: IEmojiShortcode) {
		event.preventDefault();

		this.applySelectedEmoji(shortcode);
	}

	private applySelectedEmoji(shortcode: IEmojiShortcode) {
		const textArea = <HTMLTextAreaElement>this.newMessageRef?.nativeElement;
		const caret = textArea?.selectionStart ?? (this.newMessage || "").length;

		this.applyComposerText(this.emojis.select(shortcode, this.newMessage || "", caret));
	}

	/** Writes a rewritten line back into the composer and puts the caret where it belongs -- after the
	 *  view has caught up with the new text, or the caret lands against the old one. */
	private applyComposerText(result: { text: string, caret: number }) {
		const textArea = <HTMLTextAreaElement>this.newMessageRef?.nativeElement;

		this.newMessage = result.text;

		setTimeout(() => {
			textArea?.focus();
			textArea?.setSelectionRange(result.caret, result.caret);
		});
	}

	/** Clicking a name.  mousedown rather than click: the textarea mustn't lose focus first. */
	selectMention(event: MouseEvent, user: ITeamChatMemberModel) {
		event.preventDefault();

		this.applySelectedMention(user);
	}

	private applySelectedMention(user: ITeamChatMemberModel) {
		const textArea = <HTMLTextAreaElement>this.newMessageRef?.nativeElement;
		const caret = textArea?.selectionStart ?? (this.newMessage || "").length;

		this.applyComposerText(this.mentions.select(user, this.newMessage || "", caret));
	}

	/** Whether a name in the list is already in this chat -- the rest get pulled in when tagged. */
	isMember(user: ITeamChatMemberModel): boolean {
		return (this.activeConversation?.memberUserIds || []).indexOf(user.userId) >= 0;
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// Emoji
	////////////////////////////////////////////////////////////////////////////////////////////////
	toggleEmojiPicker(event: MouseEvent) {
		// Without this the document listener below sees the same click and closes it again.
		event.stopPropagation();

		this.isEmojiPickerOpen = !this.isEmojiPickerOpen;
	}

	/** Any click outside the picker (or either autocomplete) puts it away -- they all stop the event. */
	@HostListener("document:click")
	onDocumentClick() {
		this.isEmojiPickerOpen = false;
		this.isVolumePopoverOpen = false;
		this.mentions.close();
		this.emojis.close();
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// Notification volume
	////////////////////////////////////////////////////////////////////////////////////////////////

	/** The slider popover, opened from the speaker in the conversation list's header. */
	isVolumePopoverOpen = false;

	get notificationVolumePercent(): number {
		return Math.round(this.teamChatStore.notificationVolume * 100);
	}

	get isNotificationMuted(): boolean {
		return this.teamChatStore.isNotificationMuted;
	}

	/** Whether the chat on screen is one of the ones the user has silenced.  Per chat, and per user. */
	get isActiveConversationMuted(): boolean {
		return !!(this.view === "chat" && this.activeConversation?.isMuted);
	}

	get volumeButtonTitle(): string {
		return this.isNotificationMuted ? "Notification sound is muted" : "Notification volume";
	}

	/** Speaker reflects roughly where the slider sits, so the header says something without opening it. */
	get volumeIconClass(): string {
		if (this.isNotificationMuted || this.notificationVolumePercent === 0)
			return "fa-volume-slash";

		if (this.notificationVolumePercent < 34)
			return "fa-volume-off";

		if (this.notificationVolumePercent < 67)
			return "fa-volume-down";

		return "fa-volume-up";
	}

	toggleVolumePopover(event: MouseEvent) {
		// Without this the document listener below sees the same click and closes it again.
		event.stopPropagation();

		this.isVolumePopoverOpen = !this.isVolumePopoverOpen;
	}

	/** Keeps a click on the slider or the mute button from reaching the listener that closes it. */
	onVolumePopoverClick(event: MouseEvent) {
		event.stopPropagation();
	}

	onNotificationVolumeChange(event: Event) {
		const slider = <HTMLInputElement>event.target;
		this.teamChatStore.setNotificationVolume(parseInt(slider.value, 10) / 100);
	}

	/**
	 * Silences the chime for every chat.  Distinct from muting a conversation, which is that one chat
	 * going quiet -- this is the sound itself, and it leaves the badges alone.
	 */
	toggleNotificationMute(event?: MouseEvent) {
		event?.stopPropagation();

		this.teamChatStore.setNotificationMuted(!this.teamChatStore.isNotificationMuted);
	}

	/**
	 * The bell in an open chat's header: this chat only, stopping it badging and chiming while every
	 * other one carries on.  Nothing to do with the level the slider sets, which is shared.
	 */
	async toggleActiveMute() {
		if (!this.activeConversation) return;

		await this.teamChatStore.setMuted(this.activeConversation.teamChatConversationId, !this.activeConversation.isMuted);
	}

	/**
	 * Drops the emoji in at the caret rather than on the end, so it can go mid-sentence, and leaves
	 * the caret sitting just after it ready to keep typing.  Picking one puts the picker away.
	 */
	insertEmoji(emoji: string) {
		// Closed before the insert, and not by the document listener: the panel stops the click from
		// ever reaching it.  Ahead of the early return below so it closes whether or not the textarea
		// is there to insert into.
		this.isEmojiPickerOpen = false;

		const textArea = <HTMLTextAreaElement>this.newMessageRef?.nativeElement;
		const text = this.newMessage || "";

		if (!textArea) {
			this.newMessage = text + emoji;
			return;
		}

		const selectionStart = textArea.selectionStart ?? text.length;
		const selectionEnd = textArea.selectionEnd ?? text.length;

		this.newMessage = text.substring(0, selectionStart) + emoji + text.substring(selectionEnd);

		setTimeout(() => {
			textArea.focus();
			const caret = selectionStart + emoji.length;
			textArea.setSelectionRange(caret, caret);
		});
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// Attachments
	////////////////////////////////////////////////////////////////////////////////////////////////
	pickFiles() {
		this.fileInputRef?.nativeElement?.click();
	}

	async onFilesPicked(event: Event) {
		const input = <HTMLInputElement>event.target;
		const files = Array.from(input.files || []);

		// Cleared straight away so picking the same file twice in a row still fires a change event.
		input.value = "";

		await this.sendAttachments(files);
	}

	private async sendAttachments(files: File[]) {
		if (files.length === 0 || !this.activeConversation || this.isUploading)
			return;

		this.isEmojiPickerOpen = false;
		this.isUploading = true;

		try {
			const messages = await this.teamChatStore.sendAttachments(this.activeConversation.teamChatConversationId, files);

			// An upload that failed comes back with nothing to show, and a chat message that silently
			// never arrives is worse than an error -- the sender thinks the crew has seen it.
			if (messages.length === 0) {
				Swal.fire({
					icon: "error",
					title: "Not sent",
					text: files.length > 1 ? "Those files couldn't be uploaded.  Please try again." : "That file couldn't be uploaded.  Please try again.",
					confirmButtonColor: "#007bff",
					width: "28em",
					heightAuto: false
				});
				return;
			}

			// Same as sendMessage: in debug there's no SignalR echo, so add ours in directly.
			for (const message of messages) {
				if (this.activeConversation && !this.activeConversation.messages.some(x => x.teamChatMessageId === message.teamChatMessageId))
					this.activeConversation.messages.push(message);
			}

			this.scrollToBottom();
		} finally {
			this.isUploading = false;
		}
	}

	isImage(message: ITeamChatMessageModel): boolean {
		return this.teamChatStore.isImageAttachment(message);
	}

	openAttachment(message: ITeamChatMessageModel) {
		if (message?.attachmentUrl)
			window.open(message.attachmentUrl, "_blank");
	}

	/**
	 * Scrolling back through the thread.  Prepending messages pushes everything down by exactly the
	 * height of what was added, so the reader would be thrown backwards unless the scroll position is
	 * corrected -- hence measuring scrollHeight either side and re-anchoring by the difference.
	 */
	async onMessagesScroll() {
		const messagesEl = this.messagesRef?.nativeElement;

		if (!messagesEl)
			return;

		// Reading the latest, or scrolled back through history?  Updated ahead of the early return below
		// so it tracks every scroll, not just the ones near the top.  Content growing underneath the
		// reader fires no scroll event, which is exactly what lets a photo finishing still count as
		// "they were at the bottom".
		const distanceFromBottom = messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight;
		this.isPinnedToBottom = (distanceFromBottom <= TeamChatWidgetComponent.PINNED_TO_BOTTOM_TOLERANCE);

		if (messagesEl.scrollTop > TeamChatWidgetComponent.LOAD_MORE_SCROLL_THRESHOLD)
			return;

		const previousScrollHeight = messagesEl.scrollHeight;
		const previousScrollTop = messagesEl.scrollTop;

		const loadedCount = await this.teamChatStore.loadOlderMessages();
		if (loadedCount === 0)
			return;

		// After the view has rendered the new messages, not before.
		setTimeout(() => {
			messagesEl.scrollTop = previousScrollTop + (messagesEl.scrollHeight - previousScrollHeight);
		});
	}

	private scrollToBottom() {
		this.isPinnedToBottom = true;

		setTimeout(() => {
			const messagesEl = this.messagesRef?.nativeElement;
			if (messagesEl) messagesEl.scrollTop = messagesEl.scrollHeight;

			// Still opening: every photo that lands scrolls again, so keep pushing the reveal back until
			// they stop arriving.  Once shown it stays shown -- this is only about the opening frames.
			if (!this.isThreadReady) {
				clearTimeout(this.threadSettleTimer);
				this.threadSettleTimer = setTimeout(() => this.revealThread(), TeamChatWidgetComponent.THREAD_SETTLE_DELAY);
			}
		}, 50);
	}

	/** Hides the thread and starts the clock on showing it again.  Called as a chat is opened, before
	 *  anything has been loaded into it. */
	private beginThreadOpen() {
		this.isThreadReady = false;
		clearTimeout(this.threadSettleTimer);
		clearTimeout(this.threadRevealTimer);
		this.threadSettleTimer = null;

		this.threadRevealTimer = setTimeout(() => this.revealThread(), TeamChatWidgetComponent.THREAD_REVEAL_TIMEOUT);
	}

	private revealThread() {
		this.isThreadReady = true;

		clearTimeout(this.threadSettleTimer);
		clearTimeout(this.threadRevealTimer);
		this.threadSettleTimer = null;
		this.threadRevealTimer = null;
	}

	/**
	 * A photo has no height until it has loaded, so every scroll to the bottom that ran before it
	 * arrived stopped short of where the bottom actually is.  Going again once it's there is what puts
	 * it on screen -- and, on open, what walks the thread down as a screenful of them come in.
	 *
	 * Only while the thread is pinned to the bottom: an old photo loading while somebody is scrolled
	 * back through history mustn't yank them away from what they're reading.
	 */
	onAttachmentLoaded() {
		if (this.isPinnedToBottom)
			this.scrollToBottom();
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// Renaming (anyone in the group)
	////////////////////////////////////////////////////////////////////////////////////////////////
	startRename() {
		if (!this.activeConversation) return;

		this.renameText = this.activeConversation.name || "";
		this.isRenaming = true;
	}

	cancelRename() {
		this.isRenaming = false;
	}

	async saveRename() {
		if (!this.activeConversation) return;

		const name = (this.renameText || "").trim();
		this.isRenaming = false;

		if (name === (this.activeConversation.name || "")) return;

		this.activeConversation.name = name || null;
		await this.teamChatStore.renameConversation(this.activeConversation.teamChatConversationId, name);
	}

	onRenameKeyDown(event: KeyboardEvent) {
		if (event.key === "Enter") {
			event.preventDefault();
			this.saveRename();
		} else if (event.key === "Escape") {
			event.preventDefault();
			this.cancelRename();
		}
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// New chat / add people
	////////////////////////////////////////////////////////////////////////////////////////////////
	async showNewChat() {
		this.membersForConversationId = null;
		this.newChatName = "";
		this.selectedUserIds = [];
		this.selectedTeamIds = [];
		this.currentTeamIds = [];
		this.currentUserIds = [];
		this.memberSearchText = "";
		this.setView("members");
		await this.loadPickerLists();
	}

	async showEditMembers() {
		if (!this.activeConversation) return;

		this.membersForConversationId = this.activeConversation.teamChatConversationId;
		this.newChatName = this.activeConversation.name || "";
		// The by-name members, not everybody in the chat: the rest are there through a team, and
		// showing them ticked would turn them into by-name members the moment this was saved.
		this.selectedUserIds = [...(this.activeConversation.directMemberUserIds || [])];
		this.selectedTeamIds = [...(this.activeConversation.teamIds || [])];

		// Everybody in the chat for the section at the top, not just the ones picked by name -- from
		// in here, somebody who arrived with a team is as much "in this chat" as anybody else.
		this.currentTeamIds = [...(this.activeConversation.teamIds || [])];
		this.currentUserIds = [...(this.activeConversation.memberUserIds || [])];
		this.memberSearchText = "";
		this.setView("members");
		await this.loadPickerLists();
	}

	private async loadPickerLists() {
		this.availableTeams = await this.teamChatStore.getAvailableTeams();
		this.availableUsers = await this.teamChatStore.getAvailableUsers();
		this.applyTeamCoverage();
	}

	cancelMembers() {
		this.setView(this.membersForConversationId ? "chat" : "list");
	}

	/** The teams already on the chat, top of the list.  Matched on the team's own name only -- typing
	 *  a person's name is a search for that person, and the people lists are where they'll be. */
	get filteredCurrentTeams(): ITeamChatTeamModel[] {
		return this.availableTeams
			.filter(x => this.currentTeamIds.indexOf(x.teamId) >= 0)
			.filter(x => this.matchesSearch(x.teamName));
	}

	/** The people already in the chat, top of the list, whether by name or through a team. */
	get filteredCurrentUsers(): ITeamChatMemberModel[] {
		return this.availableUsers
			.filter(x => x.userId !== this.userId)
			.filter(x => this.currentUserIds.indexOf(x.userId) >= 0)
			.filter(x => this.matchesSearch(x.userFullName));
	}

	/** The teams that could be added.  The ones already on the chat are listed above rather than
	 *  twice, which would be the same row with the same tick in two places. */
	get filteredTeams(): ITeamChatTeamModel[] {
		return this.availableTeams
			.filter(x => this.currentTeamIds.indexOf(x.teamId) < 0)
			.filter(x => this.matchesSearch(x.teamName));
	}

	private matchesSearch(text: string): boolean {
		const searchText = (this.memberSearchText || "").toLowerCase().trim();

		return !searchText || (text || "").toLowerCase().indexOf(searchText) >= 0;
	}

	/** Nothing to show at all -- everything is either filtered out or already in the chat. */
	get hasNoPickerResults(): boolean {
		return this.filteredCurrentTeams.length === 0 && this.filteredCurrentUsers.length === 0 &&
			this.filteredTeams.length === 0 && this.filteredUsers.length === 0;
	}

	toggleCurrentCollapsed() {
		this.isCurrentCollapsed = !this.isCurrentCollapsed;
	}

	toggleTeamsCollapsed() {
		this.isTeamsCollapsed = !this.isTeamsCollapsed;
	}

	togglePeopleCollapsed() {
		this.isPeopleCollapsed = !this.isPeopleCollapsed;
	}

	isTeamSelected(team: ITeamChatTeamModel): boolean {
		return this.selectedTeamIds.indexOf(team.teamId) >= 0;
	}

	toggleTeam(team: ITeamChatTeamModel) {
		if (this.isTeamSelected(team)) this.selectedTeamIds = this.selectedTeamIds.filter(x => x !== team.teamId);
		else this.selectedTeamIds = [...this.selectedTeamIds, team.teamId];

		this.applyTeamCoverage();
	}

	/** Whether one of the ticked teams already puts this person in the chat. */
	isCoveredByTeam(user: ITeamChatMemberModel): boolean {
		return this.teamCoveredUserIds.indexOf(user.userId) >= 0;
	}

	/** The ticked team(s) they're on, for the note beside their name. */
	coveringTeamNames(user: ITeamChatMemberModel): string {
		return this.teamNamesByUserId[user.userId] || "";
	}

	/** Re-reads who the ticked teams cover.  Cheap enough to redo outright on every tick -- it's a
	 *  handful of teams -- and there's no partial state to get wrong that way. */
	private applyTeamCoverage() {
		const selectedTeams = this.availableTeams.filter(x => this.selectedTeamIds.indexOf(x.teamId) >= 0);

		this.teamCoveredUserIds = [];
		this.teamNamesByUserId = {};

		for (const team of selectedTeams) {
			for (const userId of team.memberUserIds || []) {
				if (this.teamCoveredUserIds.indexOf(userId) < 0)
					this.teamCoveredUserIds.push(userId);

				// Somebody on two of the ticked teams is listed as being on both -- it's why they're
				// still in the chat when one of them drops them.
				this.teamNamesByUserId[userId] = this.teamNamesByUserId[userId]
					? `${this.teamNamesByUserId[userId]}, ${team.teamName}`
					: team.teamName;
			}
		}
	}

	/** The people who could be added -- everybody in the company bar me and bar whoever is already in
	 *  the chat, who are listed above. */
	get filteredUsers(): ITeamChatMemberModel[] {
		return this.availableUsers
			.filter(x => x.userId !== this.userId)
			.filter(x => this.currentUserIds.indexOf(x.userId) < 0)
			.filter(x => this.matchesSearch(x.userFullName));
	}

	isSelected(user: ITeamChatMemberModel): boolean {
		return this.selectedUserIds.indexOf(user.userId) >= 0;
	}

	toggleUser(user: ITeamChatMemberModel) {
		if (this.isSelected(user)) this.selectedUserIds = this.selectedUserIds.filter(x => x !== user.userId);
		else this.selectedUserIds = [...this.selectedUserIds, user.userId];
	}

	/** A chat needs somebody else in it, whether they were picked by name or came with a team. */
	get canSaveMembers(): boolean {
		const others = this.selectedUserIds
			.concat(this.teamCoveredUserIds)
			.filter(x => x !== this.userId);

		return others.length > 0;
	}

	async saveMembers() {
		if (!this.canSaveMembers) return;

		const name = (this.newChatName || "").trim();

		if (this.membersForConversationId) {
			await this.teamChatStore.updateMembers(this.membersForConversationId, this.selectedUserIds, this.selectedTeamIds);
			this.setView("chat");
			await this.teamChatStore.loadActiveConversation(this.membersForConversationId);
			return;
		}

		const conversation = await this.teamChatStore.startConversation(name, this.selectedUserIds, this.selectedTeamIds);
		if (!conversation) {
			this.showList();
			return;
		}

		this.setView("chat");
		await this.teamChatStore.loadActiveConversation(conversation.teamChatConversationId);
		this.scrollToBottom();
		setTimeout(() => this.newMessageRef?.nativeElement?.focus(), 100);
	}

	////////////////////////////////////////////////////////////////////////////////////////////////
	// Display helpers
	////////////////////////////////////////////////////////////////////////////////////////////////
	getInitials(fullName: string): string {
		if (!fullName) return "?";

		return fullName
			.split(" ")
			.filter(x => !!x)
			.slice(0, 2)
			.map(x => x[0].toUpperCase())
			.join("");
	}

	badgeText(count: number): string {
		return count > 99 ? "99+" : `${count}`;
	}
}
